import { ChangeEvent, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useParams } from 'react-router-dom';
import Loading from '../components/Loading';
import { ClinicData } from '../interface/interface';
import {
  createClinic,
  getSingleClinicInfo,
  updateClinic,
} from '../service/clinic';
import { clinicValidator } from '../validator';
import { useNavigate } from 'react-router-dom';
import { AssociatedDoctors } from '../components/AssociatedDoctors';
import showToast from '../components/ToastComponent';
import { validateEmail } from '../utils/validateEmail';

const Clinic = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { clinicId } = useParams();
  const [clinicData, setClinicData] = useState<ClinicData>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const fetchClinicData = async (clinicId: string, access_token: string) => {
    const response = await getSingleClinicInfo(clinicId, access_token);

    if (typeof response === 'number') {
      setLoading(false);
      return;
    }

    const { data } = response;

    setClinicData({
      name: data?.name,
      hospital_provider_number: data?.hospital_provider_number,
      hpio: data?.hpio,
      email: data?.email,
      phone: data?.phone,
      sms: data?.sms,
      address: data?.address,
      city: data?.city,
      state: data?.state,
      zipcode: data?.zipcode,
    });
    setLoading(false);
  };

  useEffect(() => {
    if (!user?.access_token || !clinicId) {
      console.warn('Access Token or Clinic Id Not found');
      return;
    }
    setLoading(true);
    fetchClinicData(clinicId, user.access_token);
  }, [user?.access_token]);

  if (loading) {
    <Loading />;
  }

  const createOrUpdateClinic = async (e: { preventDefault: () => void }) => {
    e.preventDefault();

    if (!user?.access_token) {
      console.warn('Clinic Id or User access token not found');
      return;
    }

    const validate = clinicValidator(clinicData);

    if (!clinicData || validate) {
      setError(true);
      return;
    }
    setError(false);

    if (!clinicId) {
      const res = await createClinic(
        clinicData as ClinicData,
        user?.access_token,
      );

      if (!res) {
        showToast({
          message: 'Failed to create clinic',
          type: 'warning',
        });
        return;
      }

      if (res.status === 201) {
        showToast({
          message: 'Clinic created successfully',
          type: 'success',
        });
        navigate('/clinics');
      } else {
        showToast({
          message: 'Failed to create clinic',
          type: 'warning',
        });
      }
      return;
    }

    const status = await updateClinic(clinicId, clinicData, user.access_token);

    if (status === 200) {
      showToast({
        message: 'Clinic details updated successfully.',
        type: 'success',
      });
      navigate('/clinics');
    } else {
      showToast({
        message: 'Failed to update clinic details',
        type: 'warning',
      });
    }
  };

  const handleInput = (e: ChangeEvent<HTMLInputElement>) => {
    setClinicData({
      ...clinicData,
      [e.target.name]: e.target.value,
    } as ClinicData);
  };

  return (
    <>
      <div className="space-y-10 divide-y divide-gray-900/10 add-patient">
        <div className="p-7 bg-secondary-pink-50 shadow rounded-xl mb-10">
          <h3 className="mb-3 text-lg font-semibold text-primary-blue-500">
            Clinic Information
          </h3>
          <div className="grid  grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 px-6 py-7 bg-white rounded-md">
            <div className="sm:col-span-2">
              <label
                htmlFor="name"
                className="text-sm font-semibold  text-secondary-pink-700"
              >
                Name <span className="text-red-500">*</span>
              </label>
              <div className="mt-2">
                <input
                  type="text"
                  name="name"
                  id="name"
                  autoComplete="given-name"
                  className={`text-sm font-regular border  rounded-md p-2 w-full 
                  ${
                    error && !clinicData?.name
                      ? 'border-red-400'
                      : 'border-secondary-cornflowerblue-400'
                  }
                  `}
                  value={clinicData?.name || ''}
                  onChange={(e) => handleInput(e)}
                />
              </div>
              {error && !clinicData?.name && (
                <small className="text-red-600">Name is required</small>
              )}
            </div>
            <div className="sm:col-span-2">
              <label
                htmlFor="hospital_provider_number"
                className="text-sm font-semibold  text-secondary-pink-700"
              >
                Hospital provider Number <span className="text-red-500">*</span>
              </label>
              <div className="mt-2">
                <input
                  type="text"
                  name="hospital_provider_number"
                  id="hospital_provider_number"
                  autoComplete="hospital_provider_number"
                  className={`text-sm font-regular border rounded-md p-2 w-full ${
                    error && !clinicData?.hospital_provider_number
                      ? 'border-red-400'
                      : 'border-secondary-cornflowerblue-400'
                  }`}
                  value={clinicData?.hospital_provider_number || ''}
                  onChange={(e) => handleInput(e)}
                />
              </div>
              {error && !clinicData?.hospital_provider_number && (
                <small className="text-red-600">
                  Hospital provider Number is required
                </small>
              )}
            </div>
            <div className="sm:col-span-2">
              <label
                htmlFor="hpio"
                className="text-sm font-semibold  text-secondary-pink-700"
              >
                HPIO <span className="text-red-500">*</span>
              </label>
              <div className="mt-2">
                <input
                  type="number"
                  name="hpio"
                  id="hpio"
                  autoComplete="hpio"
                  className={`text-sm font-regular border  rounded-md p-2 w-full ${
                    error && !clinicData?.hpio
                      ? 'border-red-400'
                      : 'border-secondary-cornflowerblue-400'
                  }`}
                  value={clinicData?.hpio || ''}
                  onChange={(e) => handleInput(e)}
                  onKeyDown={(evt) =>
                    ['e', 'E', '+', '-'].includes(evt.key) &&
                    evt.preventDefault()
                  }
                />
              </div>
              {error && !clinicData?.hpio && (
                <small className="text-red-600">HPIO is required</small>
              )}
            </div>
            <div className="sm:col-span-2">
              <label
                htmlFor="email"
                className="text-sm font-semibold  text-secondary-pink-700"
              >
                Email <span className="text-red-500">*</span>
              </label>
              <div className="mt-2">
                <input
                  type="text"
                  name="email"
                  id="email"
                  autoComplete="email"
                  className={`text-sm font-regular border rounded-md p-2 w-full ${
                    error &&
                    !clinicData?.email &&
                    !validateEmail(clinicData?.email)
                      ? 'border-red-400'
                      : 'border-secondary-cornflowerblue-400'
                  }`}
                  value={clinicData?.email || ''}
                  onChange={(e) => handleInput(e)}
                />
              </div>
              {error && !clinicData?.email ? (
                <small className="text-red-600">Email is required</small>
              ) : error && !validateEmail(clinicData?.email) ? (
                <small className="text-red-600">Email Not in valid form</small>
              ) : (
                ''
              )}
            </div>
            <div className="sm:col-span-2">
              <label
                htmlFor="phone"
                className="text-sm font-semibold  text-secondary-pink-700"
              >
                Phone <span className="text-red-500">*</span>
              </label>
              <div className="mt-2 flex rounded-md shadow-sm">
                <span
                  className={`inline-flex items-center rounded-l-md border border-r-0 
             px-3 text-gray-500 text-sm ${
               error &&
               !clinicData?.phone &&
               !(
                 clinicData?.phone &&
                 clinicData?.phone?.split('')?.length === 10
               )
                 ? 'border-red-400'
                 : 'border-secondary-cornflowerblue-400'
             }`}
                >
                  +61
                </span>
                <input
                  type="number"
                  name="phone"
                  id="phone"
                  autoComplete="phone"
                  className={`rounded-none rounded-r-md placeholder:text-gray-400 
              text-sm font-regular border rounded-md p-2 w-full ${
                error &&
                !clinicData?.phone &&
                !(
                  clinicData?.phone &&
                  clinicData?.phone?.split('')?.length === 10
                )
                  ? 'border-red-400 focus:border-red-400'
                  : 'border-secondary-cornflowerblue-400'
              }`}
                  value={clinicData?.phone || ''}
                  placeholder="123456789"
                  onChange={(e) => handleInput(e)}
                  onKeyDown={(evt) =>
                    ['e', 'E', '+', '-'].includes(evt.key) &&
                    evt.preventDefault()
                  }
                />
              </div>
              {error && !clinicData?.phone ? (
                <small className="text-red-600">Phone is required</small>
              ) : error && !(clinicData?.phone.split('').length === 10) ? (
                <small className="text-red-600">Phone Must be 10 digit</small>
              ) : null}
            </div>

            <div className="sm:col-span-2">
              <label
                htmlFor="sms"
                className="text-sm font-semibold  text-secondary-pink-700"
              >
                SMS
              </label>
              <div className="mt-2 flex rounded-md shadow-sm">
                <span
                  className="inline-flex items-center rounded-l-md border border-r-0 
            border-secondary-cornflowerblue-400 px-3 text-gray-500 text-sm"
                >
                  +61
                </span>
                <input
                  type="number"
                  name="sms"
                  id="sms"
                  autoComplete="sms"
                  className="rounded-none rounded-r-md placeholder:text-gray-400 text-sm font-regular border 
              border-secondary-cornflowerblue-400 rounded-md p-2 w-full"
                  value={clinicData?.sms || ''}
                  placeholder="123456789"
                  onChange={(e) => handleInput(e)}
                  onKeyDown={(evt) =>
                    ['e', 'E', '+', '-'].includes(evt.key) &&
                    evt.preventDefault()
                  }
                />
              </div>
            </div>

            <div className="sm:col-span-2">
              <label
                htmlFor="address"
                className="text-sm font-semibold  text-secondary-pink-700"
              >
                Address <span className="text-red-500">*</span>
              </label>
              <div className="mt-2">
                <input
                  id="address"
                  name="address"
                  type="text"
                  autoComplete="address"
                  className={`text-sm font-regular border rounded-md p-2 w-full 
                  ${
                    error && !clinicData?.address
                      ? 'border-red-400'
                      : 'border-secondary-cornflowerblue-400'
                  }
                  `}
                  onChange={(e) => handleInput(e)}
                  value={clinicData?.address || ''}
                />
              </div>
              {error && !clinicData?.address && (
                <small className="text-red-600">Address is required</small>
              )}
            </div>

            <div className="sm:col-span-2">
              <label
                htmlFor="city"
                className="text-sm font-semibold  text-secondary-pink-700"
              >
                City <span className="text-red-500">*</span>
              </label>
              <div className="mt-2 flex rounded-md shadow-sm">
                <input
                  type="text"
                  name="city"
                  id="city"
                  className={`rounded-r-md placeholder:text-gray-400 text-sm 
              font-regular border  rounded-md p-2 w-full ${
                error && !clinicData?.city
                  ? 'border-red-400'
                  : 'border-secondary-cornflowerblue-400'
              }`}
                  onChange={(e) => handleInput(e)}
                  value={clinicData?.city || ''}
                />
              </div>
              {error && !clinicData?.city && (
                <small className="text-red-600">City is required</small>
              )}
            </div>

            <div className="sm:col-span-2">
              <label
                htmlFor="state"
                className="text-sm font-semibold  text-secondary-pink-700"
              >
                State <span className="text-red-500">*</span>
              </label>
              <div className="mt-2">
                <input
                  type="text"
                  name="state"
                  id="state"
                  autoComplete="address-level1"
                  className={`text-sm font-regular border rounded-md p-2 w-full ${
                    error && !clinicData?.state
                      ? 'border-red-400'
                      : 'border-secondary-cornflowerblue-400'
                  }`}
                  value={clinicData?.state || ''}
                  onChange={(e) => handleInput(e)}
                />
              </div>
              {error && !clinicData?.state && (
                <small className="text-red-600">State is required</small>
              )}
            </div>

            <div className="sm:col-span-2">
              <label
                htmlFor="postal-code"
                className="text-sm font-semibold  text-secondary-pink-700"
              >
                Postal code <span className="text-red-500">*</span>
              </label>
              <div className="mt-2">
                <input
                  type="number"
                  name="zipcode"
                  id="postal-code"
                  autoComplete="postal-code"
                  className={`text-sm font-regular border rounded-md p-2 w-full ${
                    error && !clinicData?.zipcode
                      ? 'border-red-400'
                      : 'border-secondary-cornflowerblue-400'
                  }`}
                  value={clinicData?.zipcode || ''}
                  onChange={(e) => handleInput(e)}
                  onKeyDown={(evt) =>
                    ['e', 'E', '+', '-'].includes(evt.key) &&
                    evt.preventDefault()
                  }
                />
              </div>
              {error && !clinicData?.zipcode && (
                <small className="text-red-600">ZIP is required</small>
              )}
            </div>
          </div>
        </div>
      </div>
      {/* Associated Doctors */}
      {user?.access_token && Number(clinicId) ? (
        <AssociatedDoctors
          accessToken={user.access_token}
          clinciId={Number(clinicId)}
        />
      ) : null}
      <div className="flex items-center justify-end gap-x-6 py-8">
        <button
          type="submit"
          onClick={createOrUpdateClinic}
          className="bg-primary-blue-500 border border-solid border-primary-blue-500 text-white px-4 py-2 w-24 rounded"
        >
          {clinicId ? 'Update' : 'Create'}
        </button>
      </div>
    </>
  );
};

export default Clinic;
